import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import { useAuthStore } from '@/store/authStore';

/**
 * THE OWNER'S FEATURE SWITCHES, as the screens see them (GET /api/features).
 *
 * Every change the interview pipeline makes to how today's screens behave sits behind one
 * of these, and every one ships OFF (backend modules/settings/featureSwitches.ts). The
 * server enforces each one on its own; the screens read them only to stop offering what the
 * server will now refuse, and to say why — so a screen that has not heard yet behaves as
 * today, never as tomorrow.
 *
 * FAIL CLOSED TO TODAY. Until the answer arrives, if the request fails, or if a value is
 * anything but a literal `true` (or a named reminder mode), a switch reads as off — the
 * server's own rule. Nothing is inferred.
 *
 * FRESHNESS. A flip on the Owner's page reaches the server within its 15-second cache; the
 * screens pick it up when a page next asks after 30 seconds, or when the window regains
 * focus. There is no polling: an office of tabs behind one NAT address shares the server's
 * per-address request limit, and the switches change a handful of times a year.
 *
 * The answer lives only in TanStack Query's cache (FEATURES_KEY), never in module state, so
 * each QueryClient — the app's, or a test's — has its own, and clearing the cache on
 * sign-out takes it too.
 */

/** Every switch, in the server's order (backend featureSwitches.ts SWITCHES). */
export const SWITCH_KEYS = [
  'STAFF_ONLY_READS',
  'TENANCY_FAIL_CLOSED',
  'SESSION_REFRESH',
  'MAIL_LOG_MANAGERS_ONLY',
  'HM_NO_TEST_LINK',
  'PIPELINE_HISTORY',
  'SUBMIT_KEEPS_STAGE',
  'REJECTED_LINKS_CLOSED',
  'REJECTED_NO_OFFER',
  'STAGE_RULES_V2',
  'HIRED_FROM_OFFERS',
  'INVITE_NO_DUPLICATES',
  'TEAM_RULES',
  'OFFER_NEEDS_HIRE_DECISION',
  'INTERVIEWS_ENABLED',
  'INTERVIEW_REMINDERS',
] as const;

export type SwitchKey = (typeof SWITCH_KEYS)[number];
export const REMINDER_MODES = ['off', 'practice', 'live'] as const;
export type ReminderMode = (typeof REMINDER_MODES)[number];
/** The on/off switches; INTERVIEW_REMINDERS alone has three modes. */
export type FlagKey = Exclude<SwitchKey, 'INTERVIEW_REMINDERS'>;
export type SwitchState = Record<FlagKey, boolean> & { INTERVIEW_REMINDERS: ReminderMode };

export interface InterviewsState {
  /** The Owner has switched Interviews on. */
  enabled: boolean;
  /** …and every switch it needs is still on; otherwise the board is read-only. */
  writable: boolean;
  reminders: ReminderMode;
}

export interface Features {
  switches: SwitchState;
  interviews: InterviewsState;
}

export const ALL_OFF: Features = Object.freeze({
  switches: Object.freeze(
    Object.fromEntries(SWITCH_KEYS.map((k) => [k, k === 'INTERVIEW_REMINDERS' ? 'off' : false])),
  ) as SwitchState,
  interviews: Object.freeze({ enabled: false, writable: false, reminders: 'off' as ReminderMode }),
}) as Features;

const isMode = (v: unknown): v is ReminderMode => (REMINDER_MODES as readonly unknown[]).includes(v);

/** Whatever the server sent, as switches: anything not literally on is off. */
export function parseFeatures(raw: unknown): Features {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const sw = src.switches && typeof src.switches === 'object' ? (src.switches as Record<string, unknown>) : {};
  const iv = src.interviews && typeof src.interviews === 'object' ? (src.interviews as Record<string, unknown>) : {};
  const switches = Object.fromEntries(
    SWITCH_KEYS.map((k) => [k, k === 'INTERVIEW_REMINDERS' ? (isMode(sw[k]) ? sw[k] : 'off') : sw[k] === true]),
  ) as SwitchState;
  return {
    switches,
    interviews: {
      enabled: iv.enabled === true,
      writable: iv.writable === true,
      reminders: isMode(iv.reminders) ? iv.reminders : 'off',
    },
  };
}

export const isOn = (features: Features, key: FlagKey): boolean => features.switches[key] === true;

export const FEATURES_KEY = ['features'] as const;

export async function fetchFeatures(): Promise<Features> {
  const res = await api.get('/features');
  return parseFeatures(res.data?.data);
}

/**
 * The switches in force, for a signed-in screen. `ready` is false until the first answer
 * (or failure) — a gate that would refuse a page should wait for it rather than redirect
 * on the all-off default.
 */
export function useFeatures(): Features & { ready: boolean } {
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const { data, isPending } = useQuery({
    queryKey: FEATURES_KEY,
    queryFn: fetchFeatures,
    enabled: signedIn,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const features = data ?? ALL_OFF;
  // Pending means no answer and no failure yet; a failure counts as an answer (all off).
  return { ...features, ready: !signedIn || !isPending };
}
